
import { Link } from 'react-router-dom';

export default function MotivationPage() {
  const theories = [
    {
      id: 'motivation-theory',
      title: '動機づけ理論',
      subtitle: 'Motivation Theory：総論',
      description: 'やる気は性格ではなく、環境との相互作用で生まれる',
      icon: 'ri-lightbulb-line',
      color: 'from-amber-500 to-orange-500',
      link: '/motivation/motivation-theory'
    },
    {
      id: 'self-determination-theory',
      title: '自己決定理論',
      subtitle: 'Self-Determination Theory：SDT',
      description: '自律性・有能感・関係性の3要素が内発的動機づけを高める',
      icon: 'ri-user-star-line',
      color: 'from-blue-500 to-cyan-500',
      link: '/motivation/self-determination-theory'
    },
    {
      id: 'intrinsic-extrinsic',
      title: '内発的動機づけ／外発的動機づけ理論',
      subtitle: 'Intrinsic & Extrinsic Motivation',
      description: '外発的（報酬・評価）は短期、内発的（意味・成長）は長期',
      icon: 'ri-heart-line',
      color: 'from-rose-500 to-pink-500',
      link: '/motivation/intrinsic-extrinsic'
    },
    {
      id: 'goal-setting',
      title: '目標設定理論',
      subtitle: 'Goal-Setting Theory',
      description: '明確性・到達可能性・フィードバックが揃って初めて目標は機能する',
      icon: 'ri-flag-line',
      color: 'from-green-500 to-emerald-500',
      link: '/motivation/goal-setting'
    },
    {
      id: 'reinforcement',
      title: '強化理論',
      subtitle: 'Reinforcement Theory',
      description: '人は「強化された行動」を繰り返す',
      icon: 'ri-repeat-line',
      color: 'from-purple-500 to-violet-500',
      link: '/motivation/reinforcement'
    },
    {
      id: 'feedback',
      title: 'フィードバック理論',
      subtitle: 'Feedback Theory',
      description: '再現可能な情報が学習を促す',
      icon: 'ri-chat-check-line',
      color: 'from-teal-500 to-cyan-500',
      link: '/motivation/feedback'
    },
    {
      id: 'nudge',
      title: 'ナッジ理論',
      subtitle: 'Nudge Theory',
      description: '人は合理的ではなく、環境に流されて行動する',
      icon: 'ri-hand-heart-line',
      color: 'from-indigo-500 to-blue-500',
      link: '/motivation/nudge'
    },
    {
      id: 'behavioral-economics',
      title: '行動経済学',
      subtitle: 'Behavioral Economics',
      description: '人は常に最適判断をしない。デフォルト・比較・視認性が行動を左右する',
      icon: 'ri-line-chart-line',
      color: 'from-orange-500 to-red-500',
      link: '/motivation/behavioral-economics'
    },
    {
      id: 'equity',
      title: '公平性理論',
      subtitle: 'Equity Theory',
      description: '人は「自分と他者の投入／報酬の比率」を無意識に比較する',
      icon: 'ri-scales-line',
      color: 'from-emerald-500 to-green-500',
      link: '/motivation/equity'
    },
    {
      id: 'burnout',
      title: 'バーンアウト理論',
      subtitle: 'Maslach Burnout Theory',
      description: '情緒的消耗・脱人格化・個人的達成感の低下の3要素で段階的に進行',
      icon: 'ri-fire-line',
      color: 'from-red-500 to-rose-500',
      link: '/motivation/burnout'
    },
    {
      id: 'emotional-labor',
      title: '感情労働',
      subtitle: 'Emotional Labor',
      description: '感情を調整しながら働く職業は消耗しやすい',
      icon: 'ri-emotion-line',
      color: 'from-pink-500 to-rose-500',
      link: '/motivation/emotional-labor'
    },
    {
      id: 'psychological-reactance',
      title: '心理的リアクタンス理論',
      subtitle: 'Psychological Reactance',
      description: '自由を奪われると、人は反発・無関心になる',
      icon: 'ri-shield-cross-line',
      color: 'from-slate-500 to-gray-500',
      link: '/motivation/psychological-reactance'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 text-white py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
              <i className="ri-fire-line text-5xl"></i>
            </div>
            <h1 className="text-5xl font-bold mb-6">動機付け</h1>
            <p className="text-xl text-white/90 leading-relaxed">
              職員のモチベーション向上と働きがいのある環境づくりのための心理学理論
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">動機付けに活用できる心理学理論</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                薬局運営において、職員のモチベーション管理は最も重要な課題の一つです。やる気は個人の性格や意識の問題ではなく、環境との相互作用で生まれます。
              </p>
              <p className="text-gray-700 leading-relaxed">
                ここでは、薬局運営に活用できる12の心理学理論を紹介します。これらの理論を理解することで、「意識改革」に頼らない、構造的なモチベーション管理が可能になります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Theories Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {theories.map((theory, index) => (
                <Link
                  key={theory.id}
                  to={theory.link}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-orange-200"
                >
                  <div className="p-6">
                    <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${theory.color} rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${theory.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="mb-2">
                      <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {theory.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3 font-medium">
                      {theory.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {theory.description}
                    </p>
                    <div className="mt-4 flex items-center text-orange-600 text-sm font-semibold group-hover:translate-x-2 transition-transform">
                      詳しく見る
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-16 bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">動機付け理論活用の3つのポイント</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-orange-200">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-user-heart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">個人問題にしない</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  やる気低下を個人の性格や意識の問題として捉えず、環境や構造の課題として分析する
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-orange-200">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-settings-3-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">環境を設計する</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  「意識改革」に頼らず、自然とモチベーションが高まる環境や仕組みを設計する
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 border border-orange-200">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">継続的に測定する</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  施策の効果を定期的に測定し、PDCAサイクルを回して改善を続ける
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 rounded-2xl shadow-2xl p-12 text-white">
              <h2 className="text-3xl font-bold mb-4">まずは1つの理論から始めましょう</h2>
              <p className="text-lg text-white/90 mb-8 leading-relaxed">
                すべての理論を一度に実践する必要はありません。まずは自分の薬局の課題に最も関連する理論を1つ選び、小さく始めてみましょう。
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-xl font-semibold hover:bg-orange-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-home-line mr-2"></i>
                ホームに戻る
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
